import { forwardRef, HTMLAttributes, ReactNode } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Link } from '@vk-hr-tek/ui/Link';
import { Typography } from '@vk-hr-tek/ui/Typography';

import { modules } from '@app/modules';

import { useSearchQueryParams, useUrlWithPrefix } from '../../../hooks';

import { LoadItemsFilter } from './SearchInput.types';

export interface ListBoxCategory {
  label: string;
  value: string;
}

/**
 * Компонент ListBox представляет собой контейнер для списка элементов.
 *
 * @param filter - Фильтр для загрузки элементов.
 * @param children - Дочерние элементы, которые будут отображаться в списке.
 * @param onClose - Функция, которая будет вызвана при клике на кнопку.
 * @param ref - Реф для доступа к DOM-элементу компонента.
 * @returns JSX-элемент.
 */
export const ListBox = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLUListElement> & {
    filter: LoadItemsFilter;
    children?: ReactNode | ReactNode[];
    onClose: () => void;
  }
>(({ filter, children, onClose, ...rest }, ref) => {
  const urlWithPrefix = useUrlWithPrefix();
  const queryParams = useSearchQueryParams(filter);

  const hrefGlobalSearch = `/${modules.search.basename}${queryParams}`;

  if (!children) {
    return null;
  }

  return (
    <div ref={ref}>
      <Box display="flex" flexDirection="column">
        <ul {...rest} className={rest.className}>
          {children}
        </ul>
        <Box
          display="flex"
          alignItems="start"
          p="16"
          borderTop={1}
          borderColor="stroke.primary"
          width="100%"
          onClick={onClose}
        >
          <Link
            {...urlWithPrefix(hrefGlobalSearch)}
            variant="simple"
            size="medium"
          >
            <Typography variant="subtitle2" color="original.brand.primary">
              Перейти ко всем результатам
            </Typography>
          </Link>
        </Box>
      </Box>
    </div>
  );
});
